<script setup lang="ts">
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { getLabel } from '@/utils/data-display-utils';

import useResponsive from '@/utils/responsive-util';
import { useCommunityStore } from '@/store/communityStore';
import { getTranslation } from '@/utils/data-display-utils';
import { getAltText } from '@/utils/image.helper';
import { Locales } from '@/store/language.interface';
import FormatLineBreaks from '@/components/FormatLineBreaks.vue';

import FooterSubscribe from '../FooterSubscribe.vue';

import Facebook from '@/assets/images/facebook.svg';
import Instagram from '@/assets/images/instagram.svg';
import LinkedIn from '@/assets/images/linkedin.svg';
import X from '@/assets/images/x-twitter.svg';

//CONST
const { isMobile } = useResponsive();

// REFS
const currentYear = ref(new Date().getFullYear());

// PROPS
const props = defineProps<{
  languageEnabled?: Locales[];
}>();

// STORE
const communityStore = useCommunityStore();
const { community } = storeToRefs(communityStore);

// COMPUTED
const hasEmail = computed(() => (community && community.value.contactInfo?.email ? true : false));
const hasPhoneNumber = computed(() => (community && community.value.contactInfo?.phone ? true : false));
const hasWebsite = computed(() =>
  community && getTranslation(community.value.contactInfo, 'website', props.languageEnabled) ? true : false,
);
const hasAddress = computed(() =>
  community && getTranslation(community.value.contactInfo, 'address', props.languageEnabled) ? true : false,
);
const hasFooterDescription = computed(() =>
  community && getTranslation(community.value, 'footerDescription', props.languageEnabled) ? true : false,
);

const hasSocialNetworks = computed(() => {
  return (
    community.value.options.social_networks?.facebook?.enabled ||
    community.value.options.social_networks?.instagram?.enabled ||
    community.value.options.social_networks?.linkedin?.enabled ||
    community.value.options.social_networks?.x?.enabled
  );
});

const hasContactInfo = computed(() => {
  return hasEmail.value || hasPhoneNumber.value || hasWebsite.value || hasAddress.value;
});

const communityLogo = computed(() =>
  community.value.horizontalLogoFile?.url ? community.value.horizontalLogoFile : community.value.logoFile,
);

const hasHorizontalLogo = computed(() => Boolean(community.value.horizontalLogoFile?.url));
</script>

<template>
  <div class="footer">
    <div class="footer__content-wrapper">
      <div class="footer__content">
        <div class="col about">
          <img
            :src="communityLogo.url"
            :alt="getAltText(communityLogo, 'alt-text.community.banner', community?.languageEnabled)"
            class="horizontal-logo"
            :class="{ 'community-logo': !hasHorizontalLogo }"
          />
          <div class="sub-title">
            {{ getLabel('footer.about', props.languageEnabled) }}
          </div>
          <div v-if="hasFooterDescription">
            <FormatLineBreaks :text="getTranslation(community, 'footerDescription', props.languageEnabled)" />
          </div>
          <div v-else>
            <FormatLineBreaks :text="getTranslation(community, 'description', props.languageEnabled)" />
          </div>
          <div v-if="hasSocialNetworks" class="social-networks">
            <div v-if="community.options.social_networks.facebook.enabled">
              <a target="_blank" :href="community.options.social_networks.facebook.url">
                <img :src="Facebook" />
              </a>
            </div>
            <div v-if="community.options.social_networks.instagram.enabled">
              <a target="_blank" :href="community.options.social_networks.instagram.url">
                <img :src="Instagram" />
              </a>
            </div>
            <div v-if="community.options.social_networks.linkedin.enabled">
              <a target="_blank" :href="community.options.social_networks.linkedin.url">
                <img :src="LinkedIn" />
              </a>
            </div>
            <div v-if="community.options.social_networks.x.enabled">
              <a target="_blank" :href="community.options.social_networks.x.url">
                <img :src="X" />
              </a>
            </div>
          </div>
        </div>
        <div v-if="hasContactInfo" class="col join-us">
          <div class="content">
            <div class="sub-title">{{ getLabel('footer.join-us', props.languageEnabled) }}</div>
            <a
              v-if="hasWebsite"
              :href="getTranslation(community.contactInfo, 'website', props.languageEnabled)"
              target="_blank"
            >
              <q-icon name="language" />
              {{ getTranslation(community.contactInfo, 'website', props.languageEnabled) }}
            </a>
            <a v-if="hasEmail" :mailto="community.contactInfo?.email" class="email">
              <q-icon name="email" />
              {{ community.contactInfo?.email }}
            </a>
            <div v-if="hasPhoneNumber">
              <q-icon name="phone" />
              {{ community.contactInfo?.phone }}
            </div>
            <div v-if="hasAddress">
              <q-icon name="create" />
              <FormatLineBreaks :text="getTranslation(community.contactInfo, 'address', props.languageEnabled)" />
            </div>
          </div>
        </div>
        <div class="col subscribe">
          <footer-subscribe :community-id="community.id" :language-enabled="props.languageEnabled" />
        </div>
      </div>
    </div>
    <div class="footer__banner-wrapper">
      <div class="footer__banner">
        <div class="footer__banner-left">
          <div class="copyright">
            ©&nbsp;{{ currentYear }}&nbsp;Cocoriko<span v-if="!isMobile" class="separator">|</span>
          </div>
          <div class="terms">
            <a v-if="!isMobile" :href="getLabel('terms.terms-of-service-link', props.languageEnabled)" target="_blank">
              {{ getLabel('terms.terms-of-service', props.languageEnabled) }}
            </a>
            <a v-else :href="getLabel('terms.terms-of-service-link', props.languageEnabled)" target="_blank">
              {{ getLabel('terms.mobile.terms-of-service', props.languageEnabled) }}
            </a>
            |
            <a v-if="!isMobile" :href="getLabel('terms.privacy-policy-link', props.languageEnabled)" target="_blank">
              {{ getLabel('terms.privacy-policy', props.languageEnabled) }}
            </a>
            <a v-else :href="getLabel('terms.privacy-policy-link', props.languageEnabled)" target="_blank">
              {{ getLabel('terms.mobile.privacy-policy', props.languageEnabled) }}
            </a>
            |
            <a v-if="!isMobile" :href="getLabel('terms.moderation-policy-link', props.languageEnabled)" target="_blank">
              {{ getLabel('terms.moderation-policy', props.languageEnabled) }}
            </a>
            <a v-else :href="getLabel('terms.moderation-policy-link', props.languageEnabled)" target="_blank">
              {{ getLabel('terms.mobile.moderation-policy', props.languageEnabled) }}
            </a>
          </div>
        </div>
        <div class="footer__banner-right">
          <a href="https://www.cocoriko.org" class="no-border" target="_blank">
            <img class="footer__logo" src="@/assets/logos/logo-cocoriko.svg" alt="logo-cocoriko" />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.footer {
  display: flex;
  flex-direction: column;

  &__banner {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: $space-md;
    height: 80px;
    width: 82%;

    &-wrapper {
      display: flex;
      justify-content: center;
      width: 100%;
      background-color: $color-primary-darken;
    }

    &-left {
      display: flex;
      justify-content: start;
      align-items: center;
      color: $color-neutral-white;

      .copyright {
        display: flex;
        flex-direction: row;
        gap: $space-xs;

        .separator {
          margin-left: $space-sm;
        }
      }

      .terms {
        display: flex;
        flex-direction: row;
        align-items: center;
        padding-left: $space-sm;
      }

      a {
        color: $color-neutral-white;
        margin: 0 $space-xs;
        text-align: center;

        &:hover {
          color: $color-neutral-white;
          text-decoration: underline;
        }
      }
    }

    &-right {
      display: flex;
      justify-content: end;
      margin-left: auto;
    }
  }

  &--logo {
    height: 22px;
    margin-bottom: $space-sm;
  }

  &__content {
    display: flex;
    flex-direction: row;
    width: 82%;
    padding: 48px 0;
    gap: 48px;

    &-wrapper {
      display: flex;
      justify-content: center;
      width: 100%;
      background-color: $color-neutral-white;
    }

    .col {
      display: flex;
      flex-direction: column;
      gap: $space-md;
    }

    .sub-title {
      font-weight: bold;
      font-size: $sub-header;
    }

    .social-networks {
      display: flex;
      flex-direction: row;
      gap: $space-md;

      img {
        height: 24px;
      }
    }

    .join-us {
      align-items: center;

      .content {
        display: flex;
        flex-direction: column;
        justify-content: start;
        gap: $space-md;
      }

      div {
        display: flex;
        gap: $space-xs;
      }

      a {
        display: flex;
        align-items: center;
        gap: $space-xs;
        color: $color-neutral-black-text;
        cursor: pointer;
        border: none;
      }

      .q-icon {
        font-size: $header;
        margin-right: $space-sm;
      }
    }

    .email {
      text-transform: lowercase;
    }

    .horizontal-logo {
      height: 40px;
      width: fit-content;
      max-width: 100%;
    }
  }
}

@media screen and (max-width: $breakpoint-lg) {
  .footer__content {
    .footer-subscribe .user-subscribe .buttons {
      flex-direction: column;

      .q-field {
        width: 100%;
      }

      .q-btn {
        height: 44px;
      }
    }
  }
}

@media screen and (max-width: $breakpoint-md) {
  .footer {
    &__banner {
      flex-direction: column;
      height: auto;
      padding: $space-lg 0;
      gap: $space-sm;
      margin: 0 $space-2xl;

      &-left {
        width: 100%;
        flex-direction: column;
        gap: $space-xs;
        align-items: start;

        & .terms {
          padding-left: 0;

          a {
            margin: 0;
          }
        }
      }

      &-right {
        width: 100%;
        justify-content: start;

        img {
          height: 18px;
        }
      }
    }

    &__content {
      .footer-subscribe .user-subscribe .buttons {
        flex-direction: column;

        .q-field {
          width: 100%;
        }

        .q-btn {
          height: 44px;
        }
      }
    }
  }
}

@media screen and (max-width: $breakpoint-sm) {
  .footer {
    &__banner {
      flex-direction: column;
      height: auto;
      padding: $space-lg 0;
      gap: $space-sm;
      margin: 0 $space-lg;

      &-left {
        width: 100%;
        flex-direction: column;
        gap: $space-xs;

        & .terms {
          padding-left: 0;

          a {
            margin: 0;
          }
        }
      }

      &-right {
        width: 100%;
        justify-content: start;

        img {
          height: 18px;
        }
      }
    }

    &__content {
      flex-direction: column;
      gap: $space-md;
      margin: 0 $space-lg;
      padding: $space-md 0;

      .col {
        gap: $space-sm;
      }

      .join-us {
        .content {
          gap: $space-sm;
        }
      }

      .community-logo {
        width: 40px;
      }

      .join-us {
        align-items: start;
      }

      .about,
      .join-us,
      .subscribe {
        margin-top: $space-sm;
      }

      .footer-subscribe .user-subscribe .buttons {
        flex-direction: column;

        .q-field {
          width: 100%;
        }

        .q-btn {
          height: 44px;
        }
      }
    }
  }
}
</style>
